import { useState } from 'react';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { useApprovalQueue, useReviewBooking } from '@/api/ads/hooks';
import { extractErrorMessage } from '@/api/client';
import { ResourceGate } from '@/features/ResourceGate';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { npr, fmtDate } from '@/lib/format';

export function ApprovalsPage() {
  const { data, isLoading } = useApprovalQueue();
  const review = useReviewBooking();
  const [reasons, setReasons] = useState<Record<number, string>>({});

  const act = async (id: number, action: 'approve' | 'reject') => {
    try {
      await review.mutateAsync({ id, action, reason: reasons[id] });
      toast.success(action === 'approve' ? 'Booking approved & charged.' : 'Booking rejected & funds released.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <ResourceGate slug="ads.approvals">
      <PageHeader title="Approval Queue" description="Review submitted bookings and their creatives." />
      {isLoading ? (
        <Spinner label="Loading queue…" />
      ) : data?.results.length ? (
        <div className="space-y-4">
          {data.results.map((b) => (
            <Card key={b.id}>
              <CardHeader
                title={`${b.product_name} · ${b.advertiser_name ?? ''}`}
                description={`Campaign: ${b.campaign_name ?? '—'} · ${fmtDate(b.start_date)} → ${fmtDate(b.end_date)} · ${b.daypart_label ?? 'All day'}`}
                action={<Badge status={b.status}>{npr(b.total_amount)}</Badge>}
              />
              <CardBody className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  {b.creatives.length ? (
                    b.creatives.map((c) => (
                      <div key={c.id} className="w-44 rounded-md border p-2">
                        {c.image ? (
                          <img src={c.image} alt={c.name} className="h-24 w-full rounded object-cover" />
                        ) : (
                          <div className="flex h-24 w-full items-center justify-center rounded bg-muted text-xs text-muted-foreground">
                            {c.title || 'No image'}
                          </div>
                        )}
                        <p className="mt-1 truncate text-xs font-medium">{c.name || c.title || `#${c.id}`}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{c.landing_url || c.body || '—'}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No creatives attached.</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    className="max-w-xs"
                    placeholder="Rejection reason (optional)"
                    value={reasons[b.id] ?? ''}
                    onChange={(e) => setReasons((r) => ({ ...r, [b.id]: e.target.value }))}
                  />
                  <Button onClick={() => act(b.id, 'approve')} loading={review.isPending}>
                    <Check className="h-4 w-4" /> Approve
                  </Button>
                  <Button variant="danger" onClick={() => act(b.id, 'reject')} loading={review.isPending}>
                    <X className="h-4 w-4" /> Reject
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardBody className="py-12 text-center text-sm text-muted-foreground">
            Nothing awaiting review. 🎉
          </CardBody>
        </Card>
      )}
    </ResourceGate>
  );
}
